import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const FloorPlansPopup = ({ image, onClose, allImages, currentIndex, onNavigate }) => {
  useEffect(() => {
    // Only prevent body scroll when popup is actually open
    if (image) {
      document.body.style.overflow = 'hidden';
    }
    
    // Cleanup function - always restore scroll
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [image]); // Only run when image changes

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrev();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  const handleNext = () => {
    if (currentIndex < allImages.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };

  useEffect(() => {
    // Only add event listener when popup is open
    if (image) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [image, currentIndex, allImages]); // Dependencies to ensure latest state

  // Don't render anything if no image
  if (!image) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-300 bg-black/95 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 z-10 hover:scale-110 border border-white/20"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Navigation Buttons */}
      {allImages.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 z-10 hover:scale-110 border border-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
          
          <button
            onClick={handleNext}
            disabled={currentIndex === allImages.length - 1}
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 z-10 hover:scale-110 border border-white/20 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </>
      )}

      {/* Image Counter */}
      {allImages.length > 1 && (
        <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-black/50 backdrop-blur-sm px-3 py-2 rounded-full border border-white/20">
          <span className="text-white text-sm font-medium">
            {currentIndex + 1} / {allImages.length}
          </span>
        </div>
      )}
      
      {/* Image Container - Maximized Size */}
      <div className="relative w-full h-full max-w-[95vw] max-h-[95vh] flex items-center justify-center">
        <img
          src={image.image}
          alt={image.name}
          className="w-auto h-auto max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        
        {/* Image Type Label */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-[var(--primarycolor)] px-4 py-2 rounded-lg border border-white/20">
          <span className="text-white text-sm font-medium">
            {image.name}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FloorPlansPopup